import React, { useState, useEffect } from 'react';
import styles from './SalesExportModal.module.css';
import axios from 'axios';
import { API_BASE_URL } from '../Config.js';

/**
 * SalesExportModal
 *
 * Props:
 *  - isOpen       {boolean}   — controls visibility
 *  - onClose      {function}  — called when modal is dismissed
 *
 * Internal flow:
 *  Step 1 → Choose filter combination
 *  Step 2 → Select values (multi-school / multi-item / dates)
 *  Step 3 → Sort (drag-and-drop field priority + asc/desc)          ← NEW
 *  Step 4 → (Scenario 2 only) Choose club-by dimension
 *  Step 5 → Choose Single or Multiple Excel
 *  → triggers export API calls
 */

const FILTER_COMBINATIONS = [
  { id: 'date_school',      label: 'Date + School',        icon: '📅🏫',   scenario: 1 },
  { id: 'date_item',        label: 'Date + Item',          icon: '📅📦',   scenario: 1 },
  { id: 'item',             label: 'Item Only',            icon: '📦',     scenario: 1 },
  { id: 'school',           label: 'School Only',          icon: '🏫',     scenario: 1 },
  { id: 'date_school_item', label: 'Date + School + Item', icon: '📅🏫📦', scenario: 2 },
  { id: 'school_item',      label: 'School + Item',        icon: '🏫📦',   scenario: 2 },
];

const SORT_FIELD_OPTIONS = [
  { value: 'itemType',    label: 'Item Type' },
  { value: 'description', label: 'Item Category' },
  { value: 'itemSize',    label: 'Size' },
  { value: 'itemColor',   label: 'Item Color' },
];

// Stable ID generator (module-level so it never resets between renders)
let _idCounter = 1;
const genId = () => `lvl_${_idCounter++}`;

const SalesExportModal = ({ isOpen, onClose }) => {
  // ─── Step tracking ───────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ─── Step 1 ──────────────────────────────────────────────────────────────────
  const [selectedCombination, setSelectedCombination] = useState(null);

  // ─── Step 2 ──────────────────────────────────────────────────────────────────
  const [schools, setSchools]                 = useState([]);
  const [items, setItems]                     = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedItems, setSelectedItems]     = useState([]);
  const [startDate, setStartDate]             = useState('');
  const [endDate, setEndDate]                 = useState('');
  const [loadingOptions, setLoadingOptions]   = useState(false);

  // ─── Step 3: Sorting ─────────────────────────────────────────────────────────
  const [sortStack, setSortStack]                   = useState([]);     // [{ id, field }]
  const [globalOrder, setGlobalOrder]               = useState('asc'); // 'asc' | 'desc'
  const [draggedStackIndex, setDraggedStackIndex]   = useState(null);
  const [draggedSourceField, setDraggedSourceField] = useState(null);

  // ─── Step 4 (Scenario 2 only) ─────────────────────────────────────────────
  const [clubBy, setClubBy] = useState('');

  // ─── Step 5 ──────────────────────────────────────────────────────────────────
  const [exportType, setExportType] = useState('');

  // ─── Export state ─────────────────────────────────────────────────────────
  const [exporting, setExporting]           = useState(false);
  const [exportProgress, setExportProgress] = useState([]);
  const [exportDone, setExportDone]         = useState(false);

  // ─── Derived combo flags ──────────────────────────────────────────────────
  const combo       = FILTER_COMBINATIONS.find(c => c.id === selectedCombination);
  const needsSchool = combo && ['date_school', 'school', 'date_school_item', 'school_item'].includes(combo.id);
  const needsItem   = combo && ['date_item',   'item',  'date_school_item', 'school_item'].includes(combo.id);
  const needsDate   = combo && ['date_school', 'date_item', 'date_school_item'].includes(combo.id);
  const isScenario2 = combo?.scenario === 2;

  // ─── Step labels ─────────────────────────────────────────────────────────
  const STEPS = isScenario2
    ? ['Filter', 'Select', 'Sort', 'Club By', 'Export']
    : ['Filter', 'Select', 'Sort', 'Export'];

  const currentStepIndex = step - 1;

  // The final "choose single/multiple" step number
  const exportStep = isScenario2 ? 5 : 4;

  // ─── Reset on open/close ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedCombination(null);
      setSelectedSchools([]);
      setSelectedItems([]);
      setStartDate('');
      setEndDate('');
      setSortStack([]);
      setGlobalOrder('asc');
      setClubBy('');
      setExportType('');
      setExporting(false);
      setExportProgress([]);
      setExportDone(false);
    }
  }, [isOpen]);

  // ─── Keyboard: Escape ──────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // ─── Fetch schools ────────────────────────────────────────────────────────
  useEffect(() => {
    if (step === 2 && needsSchool) {
      setLoadingOptions(true);
      const storeId = getStoreId();
      axios.get(`${API_BASE_URL}/user/filter/getSchool`, { params: { storeId } })
        .then(res => setSchools(res.data || []))
        .catch(err => console.error('Error fetching schools:', err))
        .finally(() => setLoadingOptions(false));
    }
  }, [step, needsSchool]);

  // ─── Fetch items ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (step === 2 && needsItem) {
      setLoadingOptions(true);
      const storeId = getStoreId();
      const params  = { storeId };
      if (needsSchool && selectedSchools.length === 1) {
        params.schoolCode = selectedSchools[0];
        axios.get(`${API_BASE_URL}/user/filter/school/item_type`, { params })
          .then(res => setItems(res.data || []))
          .catch(err => console.error('Error fetching filtered items:', err))
          .finally(() => setLoadingOptions(false));
      } else {
        axios.get(`${API_BASE_URL}/user/filter/item_type`, { params })
          .then(res => setItems(res.data || []))
          .catch(err => console.error('Error fetching items:', err))
          .finally(() => setLoadingOptions(false));
      }
    }
  }, [step, needsItem, selectedSchools]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const getStoreId = () => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return user?.storeId || '';
  };

  const step2Valid = () => {
    if (needsDate   && (!startDate || !endDate))     return false;
    if (needsSchool && selectedSchools.length === 0) return false;
    if (needsItem   && selectedItems.length   === 0) return false;
    return true;
  };

  const multiSelected = () => {
    if (needsSchool && selectedSchools.length > 1) return true;
    if (needsItem   && selectedItems.length   > 1) return true;
    return false;
  };

  const toggleSchool = (s) =>
    setSelectedSchools(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleItem = (i) =>
    setSelectedItems(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);

  // ─── Sorting helpers ──────────────────────────────────────────────────────
  const availableSortFields = SORT_FIELD_OPTIONS.filter(
    opt => !sortStack.some(s => s.field === opt.value)
  );

  const addSortField = (fieldValue) => {
    if (!fieldValue || sortStack.some(s => s.field === fieldValue)) return;
    setSortStack(prev => [...prev, { id: genId(), field: fieldValue }]);
  };

  const removeSortLevel = (id) =>
    setSortStack(prev => prev.filter(s => s.id !== id));

  const handleStackDragStart = (e, index) => {
    setDraggedStackIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleStackDragOver = (e, index) => {
    e.preventDefault();
    if (draggedStackIndex === null || draggedStackIndex === index) return;
    const newStack = [...sortStack];
    const item = newStack[draggedStackIndex];
    newStack.splice(draggedStackIndex, 1);
    newStack.splice(index, 0, item);
    setSortStack(newStack);
    setDraggedStackIndex(index);
  };
  const handleStackDrop      = (e) => { e.preventDefault(); setDraggedStackIndex(null); };
  const handleStackDragEnd   = ()  => setDraggedStackIndex(null);

  const handleFieldDragStart = (e, field) => {
    setDraggedSourceField(field);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const handleFieldDragEnd      = ()  => setDraggedSourceField(null);
  const handleStackAreaDragOver = (e) => {
    e.preventDefault();
    if (draggedSourceField) e.dataTransfer.dropEffect = 'copy';
  };
  const handleStackAreaDrop = (e) => {
    e.preventDefault();
    if (draggedSourceField) { addSortField(draggedSourceField); setDraggedSourceField(null); }
  };

  // ─── Step navigation ──────────────────────────────────────────────────────
  const handleStep1Next = () => { if (selectedCombination) setStep(2); };
  const handleStep2Next = () => { if (step2Valid()) setStep(3); };
  const handleStep3Next = () => {
    if (isScenario2) setStep(4);
    else { setExportType('single'); setStep(4); }
  };
  const handleStep4Next = () => { if (clubBy) setStep(5); };

  // ─── Build API params / URL ───────────────────────────────────────────────
  const buildParams = (schoolOverride, itemOverride) => {
    const storeId = getStoreId();
    const params  = { storeId };
    if (needsDate)      { params.startDate = startDate; params.endDate = endDate; }
    if (schoolOverride) params.schoolCode = schoolOverride;
    if (itemOverride)   params.itemCode   = itemOverride;
    return params;
  };

  const resolveUrl = (schoolCode, itemCode) => {
    const h = needsDate, s = !!schoolCode, i = !!itemCode;
    if (h && s && i) return `${API_BASE_URL}/sales/report/school_item_type_date`;
    if (h && s)      return `${API_BASE_URL}/sales/report/school_code_and_date`;
    if (h && i)      return `${API_BASE_URL}/sales/report/item_code_and_date`;
    if (s && i)      return `${API_BASE_URL}/sales/report/school_and_item_type`;
    if (h)           return `${API_BASE_URL}/sales/report/sales_date`;
    if (s)           return `${API_BASE_URL}/sales/report/school_code`;
    if (i)           return `${API_BASE_URL}/sales/report/item_code`;
    return `${API_BASE_URL}/report/findReportAll`;
  };

  // ─── Apply sort ───────────────────────────────────────────────────────────
  const applySorting = (data) => {
    if (!sortStack.length) return data;
    return [...data].sort((a, b) => {
      for (const level of sortStack) {
        const va = a[level.field] ?? '';
        const vb = b[level.field] ?? '';
        const sa = typeof va === 'string' ? va.toLowerCase() : va;
        const sb = typeof vb === 'string' ? vb.toLowerCase() : vb;
        let cmp = 0;
        if (sa < sb) cmp = -1;
        else if (sa > sb) cmp = 1;
        if (cmp !== 0) return globalOrder === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  };

  // ─── Export trigger ───────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    setExportProgress([]);
    const addLog = (msg, status = 'progress') =>
      setExportProgress(prev => [...prev, { msg, status }]);

    try {
      if (exportType === 'single') {
        addLog('Fetching data…');
        let allData = [];

        if (isScenario2 || (needsSchool && needsItem)) {
          for (const school of (selectedSchools.length ? selectedSchools : [''])) {
            for (const item of (selectedItems.length ? selectedItems : [''])) {
              const res = await axios.get(resolveUrl(school || null, item || null), { params: buildParams(school || null, item || null) });
              allData   = [...allData, ...(res.data || [])];
            }
          }
        } else if (needsSchool) {
          for (const school of selectedSchools) {
            const res = await axios.get(resolveUrl(school, null), { params: buildParams(school, null) });
            allData   = [...allData, ...(res.data || [])];
          }
        } else if (needsItem) {
          for (const item of selectedItems) {
            const res = await axios.get(resolveUrl(null, item), { params: buildParams(null, item) });
            allData   = [...allData, ...(res.data || [])];
          }
        } else {
          const res = await axios.get(resolveUrl(null, null), { params: buildParams(null, null) });
          allData   = res.data || [];
        }

        if (sortStack.length > 0) {
          const fieldLabels = sortStack.map(s => SORT_FIELD_OPTIONS.find(f => f.value === s.field)?.label).join(' → ');
          addLog(`Sorting by ${fieldLabels} (${globalOrder})…`);
          allData = applySorting(allData);
        }

        addLog(`Exporting ${allData.length} rows as single file…`);
        await triggerDownload(allData, 'Sales_Report.xlsx');
        addLog('✓ Export complete!', 'done');

      } else {
        const dimension = isScenario2 ? clubBy : (needsSchool ? 'school' : 'item');

        if (dimension === 'school') {
          for (const school of selectedSchools) {
            addLog(`Fetching data for school: ${school}…`);
            const itemArg = needsItem && selectedItems.length === 1 ? selectedItems[0] : null;
            const res     = await axios.get(resolveUrl(school, itemArg), { params: buildParams(school, itemArg) });
            let data      = res.data || [];
            if (data.length > 0) {
              if (sortStack.length > 0) data = applySorting(data);
              await triggerDownload(data, `Sales_Report_${school}.xlsx`);
              addLog(`✓ Exported ${school} (${data.length} rows)`, 'done');
            } else {
              addLog(`⚠ No data for school: ${school}`, 'warn');
            }
          }
        } else {
          for (const item of selectedItems) {
            addLog(`Fetching data for item: ${item}…`);
            const schoolArg = needsSchool && selectedSchools.length === 1 ? selectedSchools[0] : null;
            const res       = await axios.get(resolveUrl(schoolArg, item), { params: buildParams(schoolArg, item) });
            let data        = res.data || [];
            if (data.length > 0) {
              if (sortStack.length > 0) data = applySorting(data);
              await triggerDownload(data, `Sales_Report_${item}.xlsx`);
              addLog(`✓ Exported ${item} (${data.length} rows)`, 'done');
            } else {
              addLog(`⚠ No data for item: ${item}`, 'warn');
            }
          }
        }
        addLog('All exports complete!', 'done');
      }

      setExportDone(true);
    } catch (err) {
      console.error('Export failed:', err);
      addLog('✗ Export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  // ─── Download helper ──────────────────────────────────────────────────────
  const triggerDownload = async (data, filename) => {
    const response = await fetch(`${API_BASE_URL}/sales/export`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Export API failed');
    const blob = await response.blob();
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    await new Promise(r => setTimeout(r, 400));
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIcon}>⬇</span>
            <span className={styles.headerTitle}>Export Sales Data</span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* ── Step Indicator ─────────────────────────────────────────── */}
        <div className={styles.stepper}>
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`${styles.stepDot} ${i < currentStepIndex ? styles.stepDone : i === currentStepIndex ? styles.stepActive : ''}`}>
                <span className={styles.stepNum}>{i < currentStepIndex ? '✓' : i + 1}</span>
                <span className={styles.stepLabel}>{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`${styles.stepLine} ${i < currentStepIndex ? styles.stepLineDone : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className={styles.body}>

          {/* ═══ STEP 1: Filter combination ══════════════════════════ */}
          {step === 1 && (
            <div className={styles.stepContent}>
              <p className={styles.stepHint}>Choose the filter combination for your export:</p>
              <div className={styles.comboGrid}>
                {FILTER_COMBINATIONS.map(c => (
                  <button
                    key={c.id}
                    className={`${styles.comboCard} ${selectedCombination === c.id ? styles.comboCardSelected : ''}`}
                    onClick={() => setSelectedCombination(c.id)}
                  >
                    <span className={styles.comboIcon}>{c.icon}</span>
                    <span className={styles.comboLabel}>{c.label}</span>
                    {c.scenario === 2 && <span className={styles.comboBadge}>Multi-dim</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ═══ STEP 2: Select values ════════════════════════════════ */}
          {step === 2 && (
            <div className={styles.stepContent}>
              <p className={styles.stepHint}>
                {isScenario2
                  ? 'Select multiple schools and/or items.'
                  : 'Select values for your filter.'}
              </p>
              {loadingOptions && <div className={styles.loadingPill}>Loading options…</div>}

              {needsDate && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Date Range</label>
                  <div className={styles.dateRow}>
                    <div className={styles.dateField}>
                      <span className={styles.dateFieldLabel}>From</span>
                      <input type="date" className={styles.dateInput} value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <span className={styles.dateSep}>→</span>
                    <div className={styles.dateField}>
                      <span className={styles.dateFieldLabel}>To</span>
                      <input type="date" className={styles.dateInput} value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {needsSchool && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Schools
                    {selectedSchools.length > 0 && <span className={styles.selectedCount}>{selectedSchools.length} selected</span>}
                  </label>
                  <div className={styles.chipGrid}>
                    {schools.map(s => (
                      <button key={s} className={`${styles.chip} ${selectedSchools.includes(s) ? styles.chipSelected : ''}`} onClick={() => toggleSchool(s)}>
                        {s}
                        {selectedSchools.includes(s) && <span className={styles.chipCheck}>✓</span>}
                      </button>
                    ))}
                    {schools.length === 0 && !loadingOptions && <span className={styles.emptyHint}>No schools found.</span>}
                  </div>
                </div>
              )}

              {needsItem && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Items
                    {selectedItems.length > 0 && <span className={styles.selectedCount}>{selectedItems.length} selected</span>}
                  </label>
                  <div className={styles.chipGrid}>
                    {items.map(i => (
                      <button key={i} className={`${styles.chip} ${selectedItems.includes(i) ? styles.chipSelected : ''}`} onClick={() => toggleItem(i)}>
                        {i}
                        {selectedItems.includes(i) && <span className={styles.chipCheck}>✓</span>}
                      </button>
                    ))}
                    {items.length === 0 && !loadingOptions && <span className={styles.emptyHint}>No items found.</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ STEP 3: Sort ════════════════════════════════════════ */}
          {step === 3 && (
            <div className={styles.stepContent}>
              <p className={styles.stepHint}>
                Optionally set the sort order for exported data. Drag fields to set priority. This step can be skipped.
              </p>

              {/* Available Fields */}
              <div className={styles.sortSection}>
                <div className={styles.sortSectionTitle}>
                  <span className={styles.sortSectionIcon}>☰</span>
                  Available Fields
                  <span className={styles.sortSectionHint}>click or drag to add</span>
                </div>
                <div className={styles.sortFieldsContainer}>
                  {availableSortFields.map(opt => (
                    <div
                      key={opt.value}
                      className={`${styles.sortFieldPill} ${draggedSourceField === opt.value ? styles.sortFieldPillDragging : ''}`}
                      draggable
                      onDragStart={(e) => handleFieldDragStart(e, opt.value)}
                      onDragEnd={handleFieldDragEnd}
                      onClick={() => addSortField(opt.value)}
                    >
                      <span className={styles.sortFieldPillIcon}>⊕</span>
                      {opt.label}
                    </div>
                  ))}
                  {availableSortFields.length === 0 && (
                    <span className={styles.sortFieldsEmpty}>All fields added to sort order</span>
                  )}
                </div>
              </div>

              {/* Sort Priority Stack */}
              <div className={styles.sortSection}>
                <div className={styles.sortSectionTitle}>
                  <span className={styles.sortSectionIcon}>⇅</span>
                  Sort Priority
                  <span className={styles.sortSectionHint}>drag to reorder · first = highest priority</span>
                </div>
                <div
                  className={`${styles.sortStackZone} ${draggedSourceField ? styles.sortStackZoneActive : ''}`}
                  onDragOver={handleStackAreaDragOver}
                  onDrop={handleStackAreaDrop}
                >
                  {sortStack.length === 0 ? (
                    <div className={styles.sortStackEmpty}>
                      <span className={styles.sortStackEmptyIcon}>↕</span>
                      <span>Drop fields here or click from above to add</span>
                    </div>
                  ) : (
                    <div className={styles.sortStackList}>
                      {sortStack.map((s, i) => {
                        const label = SORT_FIELD_OPTIONS.find(f => f.value === s.field)?.label || s.field;
                        return (
                          <React.Fragment key={s.id}>
                            <div
                              className={`${styles.sortStackItem} ${draggedStackIndex === i ? styles.sortStackItemDragging : ''}`}
                              draggable
                              onDragStart={(e) => handleStackDragStart(e, i)}
                              onDragOver={(e) => handleStackDragOver(e, i)}
                              onDrop={handleStackDrop}
                              onDragEnd={handleStackDragEnd}
                            >
                              <span className={styles.sortStackHandle}>⠿</span>
                              <span className={styles.sortStackBadge}>{i + 1}</span>
                              <span className={styles.sortStackLabel}>{label}</span>
                              <button className={styles.sortStackRemove} onClick={() => removeSortLevel(s.id)}>✕</button>
                            </div>
                            {i < sortStack.length - 1 && (
                              <div className={styles.sortStackArrow}>↓</div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Sort Direction */}
              <div className={styles.sortOrderSection}>
                <div className={styles.sortSectionTitle}>
                  <span className={styles.sortSectionIcon}>↕</span>
                  Sort Direction
                </div>
                <div className={styles.sortOrderCards}>
                  <button
                    className={`${styles.sortOrderCard} ${globalOrder === 'asc' ? styles.sortOrderCardSelected : ''}`}
                    onClick={() => setGlobalOrder('asc')}
                  >
                    <span className={styles.sortOrderCardIcon}>↑</span>
                    <span className={styles.sortOrderCardLabel}>Ascending</span>
                    <span className={styles.sortOrderCardDesc}>A → Z · 0 → 9</span>
                  </button>
                  <button
                    className={`${styles.sortOrderCard} ${globalOrder === 'desc' ? styles.sortOrderCardSelected : ''}`}
                    onClick={() => setGlobalOrder('desc')}
                  >
                    <span className={styles.sortOrderCardIcon}>↓</span>
                    <span className={styles.sortOrderCardLabel}>Descending</span>
                    <span className={styles.sortOrderCardDesc}>Z → A · 9 → 0</span>
                  </button>
                </div>
              </div>

              {sortStack.length === 0 && (
                <div className={styles.sortSkipNote}>
                  💡 No fields selected — data will export in its original order.
                </div>
              )}
            </div>
          )}

          {/* ═══ STEP 4: Club By (Scenario 2 only) ═══════════════════ */}
          {step === 4 && isScenario2 && (
            <div className={styles.stepContent}>
              <p className={styles.stepHint}>When exporting multiple files, how should the data be grouped?</p>
              <div className={styles.clubByGrid}>
                <button
                  className={`${styles.clubCard} ${clubBy === 'school' ? styles.clubCardSelected : ''}`}
                  onClick={() => setClubBy('school')}
                >
                  <span className={styles.clubIcon}>🏫</span>
                  <span className={styles.clubLabel}>Club by School</span>
                  <span className={styles.clubDesc}>One Excel file per school, containing all items</span>
                </button>
                <button
                  className={`${styles.clubCard} ${clubBy === 'item' ? styles.clubCardSelected : ''}`}
                  onClick={() => setClubBy('item')}
                >
                  <span className={styles.clubIcon}>📦</span>
                  <span className={styles.clubLabel}>Club by Item</span>
                  <span className={styles.clubDesc}>One Excel file per item, containing all schools</span>
                </button>
              </div>
            </div>
          )}

          {/* ═══ Export type step (step 4 non-s2, or step 5 s2) ═══════ */}
          {step === exportStep && !exporting && !exportDone && (
            <div className={styles.stepContent}>
              <p className={styles.stepHint}>How would you like to export?</p>

              <div className={styles.exportSummary}>
                {needsDate && startDate && <span className={styles.summaryTag}>📅 {startDate} → {endDate}</span>}
                {selectedSchools.length > 0 && <span className={styles.summaryTag}>🏫 {selectedSchools.join(', ')}</span>}
                {selectedItems.length > 0 && <span className={styles.summaryTag}>📦 {selectedItems.join(', ')}</span>}
                {isScenario2 && clubBy && <span className={styles.summaryTag}>⚙ Club by {clubBy}</span>}
                {sortStack.length > 0 && (
                  <span className={styles.summaryTag}>
                    ⇅ {sortStack.map(s => SORT_FIELD_OPTIONS.find(f => f.value === s.field)?.label).join(' → ')} ({globalOrder})
                  </span>
                )}
              </div>

              <div className={styles.exportTypeGrid}>
                <button
                  className={`${styles.exportTypeCard} ${exportType === 'single' ? styles.exportTypeSelected : ''}`}
                  onClick={() => setExportType('single')}
                >
                  <span className={styles.exportTypeIcon}>📄</span>
                  <span className={styles.exportTypeLabel}>Single Excel</span>
                  <span className={styles.exportTypeDesc}>All data combined in one .xlsx file</span>
                </button>
                {(multiSelected() || isScenario2) && (
                  <button
                    className={`${styles.exportTypeCard} ${exportType === 'multiple' ? styles.exportTypeSelected : ''}`}
                    onClick={() => setExportType('multiple')}
                  >
                    <span className={styles.exportTypeIcon}>📂</span>
                    <span className={styles.exportTypeLabel}>Multiple Excel</span>
                    <span className={styles.exportTypeDesc}>
                      {isScenario2
                        ? `One file per ${clubBy} — ${clubBy === 'school' ? selectedSchools.length : selectedItems.length} file(s)`
                        : needsSchool
                          ? `${selectedSchools.length} file(s), one per school`
                          : `${selectedItems.length} file(s), one per item`}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ═══ Export Progress ══════════════════════════════════════ */}
          {(exporting || exportDone) && (
            <div className={styles.progressPanel}>
              <div className={styles.progressLog}>
                {exportProgress.map((entry, i) => (
                  <div
                    key={i}
                    className={`${styles.logRow} ${
                      entry.status === 'done'  ? styles.logDone  :
                      entry.status === 'warn'  ? styles.logWarn  :
                      entry.status === 'error' ? styles.logError :
                      styles.logProgress
                    }`}
                  >
                    {entry.status === 'progress' && <span className={styles.logSpinner} />}
                    {entry.msg}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className={styles.footer}>
          {step > 1 && !exporting && !exportDone && (
            <button className={styles.btnBack} onClick={() => setStep(s => s - 1)}>← Back</button>
          )}
          <div className={styles.footerRight}>
            {exportDone ? (
              <button className={styles.btnDone} onClick={onClose}>Close</button>
            ) : exporting ? (
              <button className={styles.btnExport} disabled><span className={styles.spinner} />Exporting…</button>
            ) : step === 1 ? (
              <button className={styles.btnNext} onClick={handleStep1Next} disabled={!selectedCombination}>Next →</button>
            ) : step === 2 ? (
              <button className={styles.btnNext} onClick={handleStep2Next} disabled={!step2Valid()}>Next →</button>
            ) : step === 3 ? (
              /* Sort step is always optional — Next is always enabled */
              <button className={styles.btnNext} onClick={handleStep3Next}>Next →</button>
            ) : step === 4 && isScenario2 ? (
              <button className={styles.btnNext} onClick={handleStep4Next} disabled={!clubBy}>Next →</button>
            ) : (
              <button className={styles.btnExport} onClick={handleExport} disabled={!exportType}>⬇ Export</button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SalesExportModal;